import { useCallback, useMemo, useState } from "react";
import type { ScoredCandidate } from "@smb/shared";
import { Card, Caveat, ErrorNote, Loading, RiskFlagList, ScoreChip, SymbolLink } from "../components/ui";
import { SECTOR_LABELS, fmtClock, fmtDelta, fmtPrice, fmtUsd, useSnapshot } from "../lib/data";
import { useQuotes, useVisibleSymbols, type LiveQuote } from "../lib/quotes";

export function Dashboard() {
  const { data, error, loading } = useSnapshot();
  const [sector, setSector] = useState<string>("all");
  const [query, setQuery] = useState("");
  const [hideFlagged, setHideFlagged] = useState(false);

  const candidates = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toUpperCase();
    return data.candidates.filter((c) => {
      if (sector !== "all" && c.meta.sector !== sector) return false;
      if (hideFlagged && c.riskFlags.some((f) => f.severity === "severe")) return false;
      if (q && !c.symbol.includes(q) && !c.meta.name.toUpperCase().includes(q)) return false;
      return true;
    });
  }, [data, sector, query, hideFlagged]);

  if (loading) return <Loading what="the latest snapshot" />;
  if (error || !data) return <ErrorNote error={error ?? "no data"} what="the snapshot" />;

  const sectors = [...new Set(data.candidates.map((c) => c.meta.sector))].sort();

  return (
    <div className="space-y-5">
      <ModelStatus />

      <div className="grid gap-5 md:grid-cols-3">
        <Card title="Market regime" subtitle={`as of ${data.asOfDate}`} className="md:col-span-2">
          <p className="text-lg font-semibold">{regimeLabel(data.macro.regime)}</p>
          <ul className="mt-2 space-y-1">
            {data.macro.drivers.map((d) => (
              <li key={d} className="text-sm" style={{ color: "var(--text-secondary)" }}>
                {d}
              </li>
            ))}
          </ul>
          <Caveat>
            Context for you to read, not an input to any score. The model does not time the market,
            and this classification is three coarse buckets from three indicators.
          </Caveat>
        </Card>

        <Card title="Coverage">
          <dl className="space-y-1.5 text-sm">
            <Row label="Scored" value={`${data.dataQuality.scored} of ${data.dataQuality.universeSize}`} />
            <Row label="Excluded by gates" value={String(data.dataQuality.excludedByGates)} />
            <Row label="Missing fundamentals" value={String(data.dataQuality.missingFundamentals)} />
            <Row label="Themes tracked" value={String(data.themes.length)} />
          </dl>
          <Caveat>
            Names are excluded for being untradeable at size, not for scoring badly.
          </Caveat>
        </Card>
      </div>

      <Card
        title="Factor ranking"
        subtitle={
          <>
            {candidates.length} of {data.candidates.length} names, ordered by composite factor score.
            This is a ranking of measured characteristics, not a list of picks.
          </>
        }
      >
        <div className="flex flex-wrap gap-2 pb-3">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by symbol or name"
            className="rounded border px-2 py-1 text-sm"
            style={{
              background: "var(--surface-0)",
              borderColor: "var(--border-strong)",
              color: "var(--text-primary)",
            }}
          />
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            className="rounded border px-2 py-1 text-sm"
            style={{
              background: "var(--surface-0)",
              borderColor: "var(--border-strong)",
              color: "var(--text-primary)",
            }}
          >
            <option value="all">All sectors</option>
            {sectors.map((s) => (
              <option key={s} value={s}>
                {SECTOR_LABELS[s] ?? s}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-sm" style={{ color: "var(--text-secondary)" }}>
            <input
              type="checkbox"
              checked={hideFlagged}
              onChange={(e) => setHideFlagged(e.target.checked)}
            />
            Hide names with a severe risk flag
          </label>
        </div>

        <CandidateTable candidates={candidates} />
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt style={{ color: "var(--text-secondary)" }}>{label}</dt>
      <dd className="tnum font-medium">{value}</dd>
    </div>
  );
}

const regimeLabel = (r: string): string =>
  r === "RiskOn" ? "Risk-on" : r === "RiskOff" ? "Risk-off" : "Neutral";

/**
 * The backtest verdict, on the landing page rather than buried.
 *
 * A ranked table is read as a recommendation no matter what the column header
 * says. If the model failed its own test, the first thing a reader sees has to
 * say so — putting that only on a separate tab would be technically honest and
 * practically misleading.
 */
function ModelStatus() {
  return (
    <div
      className="rounded-lg border-l-4 border px-4 py-3"
      style={{
        background: "var(--surface-2)",
        borderColor: "var(--border)",
        borderLeftColor: "var(--status-warning)",
      }}
    >
      <p className="text-sm font-semibold">This ranking has not been shown to predict returns.</p>
      <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
        Tested across 18 parameter settings, it beat an equal-weighted basket of the same names in
        14 and reached statistical significance in 2. Results flip sign on ordinary choices, which
        is what noise looks like. Use these scores to find things worth reading about — not as a
        forecast.{" "}
        <a href="#/backtest" className="underline underline-offset-2" style={{ color: "var(--series-1)" }}>
          See the full test
        </a>
        .
      </p>
    </div>
  );
}

/**
 * One ranked row.
 *
 * Extracted from the map it used to live in so it can hold a ref: the live
 * price column quotes what is on screen, and a row has to be able to say
 * whether it is.
 */
function CandidateRow({
  c,
  quote,
  register,
}: {
  c: ScoredCandidate;
  quote: LiveQuote | undefined;
  register: (symbol: string, el: Element | null) => void;
}) {
  // Stable per row. A ref callback rebuilt each render would make React
  // detach and reattach the observer every time, which loops forever.
  const ref = useCallback(
    (el: HTMLTableRowElement | null) => register(c.symbol, el),
    [register, c.symbol],
  );

  return (
    <tr ref={ref} className="border-t" style={{ borderColor: "var(--border)" }}>
      <td className="tnum py-2 pr-3 text-xs" style={{ color: "var(--text-muted)" }}>
        {c.rank}
      </td>
      <td className="py-2 pr-3">
        <SymbolLink symbol={c.symbol} name={c.meta.name} />
      </td>
      <td className="py-2 pr-3 text-xs" style={{ color: "var(--text-secondary)" }}>
        {SECTOR_LABELS[c.meta.sector] ?? c.meta.sector}
      </td>
      <td className="py-2 pr-3 text-right">
        <ScoreChip score={c.composite} />
      </td>
      <td className="tnum py-2 pr-3 text-right" style={{ color: "var(--text-secondary)" }}>
        {fmtUsd(c.lastPrice)}
      </td>
      <LivePriceCell close={c.lastPrice} quote={quote} />
      <td className="py-2">
        <RiskFlagList flags={c.riskFlags} compact />
      </td>
    </tr>
  );
}

/**
 * The live half of the price columns.
 *
 * An em dash when there is no quote, and never the close restyled to look
 * live. The distinction between "this is the last close" and "this is where it
 * is trading now" is the entire point of the column, so a cell that cannot
 * tell the difference shows nothing.
 */
function LivePriceCell({ close, quote }: { close: number | null; quote: LiveQuote | undefined }) {
  if (!quote) {
    return (
      <td className="tnum py-2 pr-3 text-right text-xs" style={{ color: "var(--text-muted)" }}>
        —
      </td>
    );
  }

  const delta = close === null || close <= 0 ? null : quote.price / close - 1;
  const color =
    delta === null || delta === 0
      ? "var(--text-secondary)"
      : delta > 0
        ? "var(--gain)"
        : "var(--loss)";

  return (
    <td
      className="tnum py-2 pr-3 text-right"
      style={{ color }}
      title={`Last trade ${fmtClock(quote.observedAt)}`}
    >
      {fmtPrice(quote.price)}
      <span className="block text-xs">{fmtDelta(close, quote.price)}</span>
    </td>
  );
}

function CandidateTable({ candidates }: { candidates: readonly ScoredCandidate[] }) {
  const { register, visible } = useVisibleSymbols();
  const { quotes, enabled } = useQuotes(visible);

  if (candidates.length === 0) {
    return (
      <p className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }}>
        No names match these filters.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left" style={{ color: "var(--text-muted)" }}>
            <th className="font-medium py-2 pr-3 text-xs">#</th>
            <th className="font-medium py-2 pr-3 text-xs">Name</th>
            <th className="font-medium py-2 pr-3 text-xs">Sector</th>
            <th className="font-medium py-2 pr-3 text-xs text-right">Score</th>
            <th className="font-medium py-2 pr-3 text-xs text-right">Close</th>
            <th className="font-medium py-2 pr-3 text-xs text-right">
              {enabled ? "Live" : "Live (sign in)"}
            </th>
            <th className="font-medium py-2 text-xs">Risk flags</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((c) => (
            <CandidateRow key={c.symbol} c={c} quote={quotes.get(c.symbol)} register={register} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
